import { FunctionComponent, useMemo, useRef, useState } from "react";

import { Box, Button, Drawer, DrawerContent, DrawerHeader, DrawerCloseButton, DrawerOverlay, DrawerBody, DrawerFooter } from "@chakra-ui/react";
import { formatTimestampStr, getDateFormat } from "am-tax-fe-core";

import { ConversationResponse, useCurrentUser, User, useRequestConversations } from "../../api";
import DownloadToCsvButton from "../DownloadToCsvButton";

import ConversationList from "./ConversationList";
import CreateConversationDialog from "./CreateConversationDialog";

interface Props {
    engagementId: string;
    projectId?: string;
    requestId: string;
    requestTitle: string;
    show: boolean;
    toggleShow: (show: boolean) => void;
}

const ConversationDrawer: FunctionComponent<Props> = ({ engagementId, projectId, requestId, requestTitle, show, toggleShow }: Props) => {
    // DEVNOTE: wait until we have solved issue with plain text for downloading comments before showing the download-button
    const USE_DOWNLOAD_FOR_CONVERSATION = true;
    const dateFormat = getDateFormat();

    const [showCreateConversationDialog, setShowCreateConversationDialog] = useState(false);

    const focusAfterClosingRef = useRef(null);

    const { data } = useRequestConversations(requestId, engagementId, projectId);

    const sortByCreatedDate = (c1: ConversationResponse, c2: ConversationResponse) => {
        if (!c1.dateCreated || !c2.dateCreated) return 0;
        return new Date(c1.dateCreated).getTime() > new Date(c2.dateCreated).getTime() ? -1 : 1;
    };

    const sortedConversations = useMemo(() => {
        return data ? data.sort(sortByCreatedDate) : [];
    }, [data]);

    const currentUserQuery = useCurrentUser();
    const currentUser = currentUserQuery.data as User;

    const refinedData = useMemo(() => {
        const data: { conversation: string | undefined; from: string | undefined; created: string | undefined; content: string | undefined }[] = [];
        sortedConversations.forEach(conversation => {
            data.push({
                conversation: conversation.title,
                from: undefined,
                created: undefined,
                content: undefined,
            });
            const filteredComments = !currentUser || !currentUser.isInternal ? conversation.comments.filter(x => x.isPublished) : conversation.comments;
            filteredComments.forEach(comment => {
                data.push({
                    conversation: undefined,
                    from: comment.user ? `${comment.user.firstName} ${comment.user.lastName}` : undefined,
                    created: formatTimestampStr(comment.dateCreated, { appendTime: true, dateFormat }),
                    content: comment.content,
                });
            });
        });
        return data;
    }, [currentUser, sortedConversations, dateFormat]);

    const showDownloadBtn = useMemo(() => {
        return USE_DOWNLOAD_FOR_CONVERSATION && sortedConversations.length > 0;
    }, [USE_DOWNLOAD_FOR_CONVERSATION, sortedConversations]);

    return (
        <>
            <Drawer isOpen={show} placement="right" onClose={() => toggleShow(!show)} finalFocusRef={focusAfterClosingRef} size="xl">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>Conversation History</DrawerHeader>

                    <DrawerBody>
                        <Box>
                            <ConversationList engagementId={engagementId} projectId={projectId} requestId={requestId} conversations={sortedConversations} />
                        </Box>
                    </DrawerBody>

                    <DrawerFooter justifyContent={showDownloadBtn ? "space-between" : "flex-end"}>
                        {showDownloadBtn && (
                            <DownloadToCsvButton
                                columnHeaders={["Conversation", "Author", "Posted", "Content"]}
                                data={refinedData}
                                fileName={`${requestTitle} - Conversation`}
                                debug={false}
                            />
                        )}
                        <Button m="1rem" variant="primary" onClick={() => setShowCreateConversationDialog(true)} ref={focusAfterClosingRef}>
                            Start a new Conversation
                        </Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>
            <CreateConversationDialog
                requestId={requestId}
                engagementId={engagementId}
                projectId={projectId}
                show={showCreateConversationDialog}
                toggleShow={setShowCreateConversationDialog}
                focusAfterClosingRef={focusAfterClosingRef}
            />
        </>
    );
};

export default ConversationDrawer;
